import { Box, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ListContextProvider, ResourceContextProvider, useLocale } from 'react-admin';
import { DashboardContent } from 'src/layouts/dashboard';
import { LanguageValue } from 'src/locales';
import ErrorPage403 from 'src/pages/error/403';
import { PermissionGuard } from 'src/routes/components';
import { DashboardFilter } from './components/DashboardFilter';
import { useMemo, useState } from 'react';
import { ListCard } from './components/ListCard';
import { resolveDomainUrl } from 'src/utils/api-url';
import { PredictionTradingActivityChart } from './components/PredictionTradingActivityChart';
import { PredictionUserGrowthChart } from './components/PredictionUserGrowthChart';
import { PredictionCategoryStatsChart } from './components/PredictionCategoryStatsChart';

// tradingType values: 'Perp' | 'Meme' | 'Predict' | 'TapGrid'
const GRAFANA_URL_MAP: Record<string, Partial<Record<LanguageValue, string>>> = {
  Perp: {
    en: resolveDomainUrl(process.env.REACT_APP_GRAFANA_DASHBOARD_URL_FUTURE_EN),
    cn: resolveDomainUrl(process.env.REACT_APP_GRAFANA_DASHBOARD_URL_FUTURE_CN),
  },
  Meme: {
    en: resolveDomainUrl(process.env.REACT_APP_GRAFANA_DASHBOARD_URL_MEME_EN),
    cn: resolveDomainUrl(process.env.REACT_APP_GRAFANA_DASHBOARD_URL_MEME_CN),
  },
  Predict: {
    en: resolveDomainUrl(process.env.REACT_APP_GRAFANA_DASHBOARD_URL_PREDICT_EN),
    cn: resolveDomainUrl(process.env.REACT_APP_GRAFANA_DASHBOARD_URL_PREDICT_CN),
  },
  TapGrid: {
    en: '',
    cn: '',
  },
};

dayjs.extend(utc);

export default () => {
  const locale = useLocale();
  const muiTheme = useTheme();
  const themeMode = muiTheme.palette.mode; // 'dark' | 'light'
  const defaultFromTime = dayjs.utc().subtract(30, 'day').startOf('day').toISOString();
  const defaultToTime = dayjs.utc().endOf('day').toISOString();

  const defaultFilter = {
    fromTime: defaultFromTime,
    toTime: defaultToTime,
    tradingType: 'Perp',
    depositType: 'DepositFuture',
    withdrawType: 'WithdrawFuture',
  };
  const [filterValues, setFilterValues] = useState(defaultFilter);

  const grafanaUrl = useMemo(() => {
    // Use tradingType set by DashboardFilter to pick the correct URL
    const urlsByLocale = GRAFANA_URL_MAP[filterValues.tradingType] || '';
    const baseGrafanaUrl = urlsByLocale[locale as LanguageValue] || urlsByLocale.en || '';

    if (!baseGrafanaUrl) return '';
    try {
      const url = new URL(baseGrafanaUrl);
      if (filterValues.fromTime) {
        url.searchParams.set('from', new Date(filterValues.fromTime).getTime().toString());
      }
      if (filterValues.toTime) {
        url.searchParams.set('to', new Date(filterValues.toTime).getTime().toString());
      }
      // Sync Grafana theme with project theme
      url.searchParams.set('theme', themeMode);
      return url.toString();
    } catch (e) {
      // In case baseGrafanaUrl is not a valid URL, fallback to simple append
      const separator = baseGrafanaUrl.includes('?') ? '&' : '?';
      const fromParam = filterValues.fromTime
        ? `from=${new Date(filterValues.fromTime).getTime()}`
        : '';
      const toParam = filterValues.toTime ? `to=${new Date(filterValues.toTime).getTime()}` : '';
      const themeParam = `theme=${themeMode}`;
      const queryParams = [fromParam, toParam, themeParam].filter(Boolean).join('&');
      return queryParams ? `${baseGrafanaUrl}${separator}${queryParams}` : baseGrafanaUrl;
    }
  }, [filterValues, locale, themeMode]);

  return (
    <PermissionGuard resource="dashboard" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent sx={{ paddingBottom: '0' }}>
        <ResourceContextProvider value="dashboard">
          <ListContextProvider
            value={
              {
                filterValues,
                setFilters: (filters: any) => setFilterValues((f) => ({ ...f, ...filters })),
                displayedFilters: {},
                showFilter: () => {},
                hideFilter: () => {},
                data: [],
                total: 0,
                isLoading: false,
                isPending: false,
                isFetching: false,
              } as any
            }
          >
            <DashboardFilter />
            <Box sx={{ mb: 2 }}>
              <ListCard />
            </Box>
            <PredictionCategoryStatsChart />
            <PredictionTradingActivityChart />
            <PredictionUserGrowthChart />
          </ListContextProvider>
        </ResourceContextProvider>
        {grafanaUrl && (
          <Box sx={{ height: 'calc(100vh - 160px)', width: '100%' }}>
            <iframe
              title="Grafana Dashboard"
              src={grafanaUrl}
              style={{ border: 'none', width: '100%', height: '100%' }}
            />
          </Box>
        )}
      </DashboardContent>
    </PermissionGuard>
  );
};
